/**
 * Power-basis hint — whether the owner's own words say the declared MW is IT
 * load or facility load.
 *
 * The basis is the highest-leverage screening answer: declared, it collapses
 * the dual IT/facility screening cases into one and un-withholds the facility
 * and apparent-power layers. It is also easy to get wrong silently (an earlier
 * reconciliation dropped a stated "IT load" basis), so this reader only
 * SUGGESTS — the form shows it as an accept-on-click chip — and it abstains
 * whenever the text names both bases or neither. Pure; no inference beyond
 * the explicit phrases below.
 */

export interface PowerBasisHint {
  basis: 'it-load' | 'facility';
  /** The matched phrase, quoted back to the user. */
  phrase: string;
}

/** "IT load", "critical IT load", "IT capacity", "IT MW" — IT stays upper-case. */
const IT_BASIS = /\b(?:critical\s+)?IT[\s-]+(?:load|capacity|demand|power)\b|\bIT\s+MW\b|\bcritical[\s-]+load\b/;
/** "facility load", "total facility power", "utility load". */
const FACILITY_BASIS = /\b(?:total\s+)?facility[\s-]+(?:load|demand|power|capacity)\b|\butility[\s-]+(?:load|demand)\b/i;

export function inferPowerBasisFromText(
  texts: ReadonlyArray<string | null | undefined>,
): PowerBasisHint | null {
  let it: string | null = null;
  let facility: string | null = null;
  for (const text of texts) {
    if (!text) continue;
    it ??= IT_BASIS.exec(text)?.[0] ?? null;
    facility ??= FACILITY_BASIS.exec(text)?.[0] ?? null;
  }
  if (it && !facility) return { basis: 'it-load', phrase: it };
  if (facility && !it) return { basis: 'facility', phrase: facility };
  return null;
}
